import { useState, useEffect, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import Sidebar from "./components/Sidebar";
import Dashboard from "./pages/Dashboard";
import Speisen from "./pages/Speisen";
import Getraenke from "./pages/Getraenke";
import Mitarbeiter from "./pages/Mitarbeiter";
import Fixkosten from "./pages/Fixkosten";
import Zutaten from "./pages/Zutaten";
import Einnahmen from "./pages/Einnahmen";
import Statistik from "./pages/Statistik";
import Preisvorschlag from "./pages/Preisvorschlag";
import Warenwirtschaft from "./pages/Warenwirtschaft";
import Lieferanten from "./pages/Lieferanten";
import Einstellungen from "./pages/Einstellungen";
import Unternehmerlohn from "./pages/Unternehmerlohn";
import Strategie from "./pages/Strategie";
import Schichtplan from "./pages/Schichtplan";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import AdminDashboard from "./pages/AdminDashboard";
import PricingPage from "./pages/PricingPage";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import AdminSubscriptions from "./pages/AdminSubscriptions";
import AdminBackup from "./pages/AdminBackup";
import LandingPage from "./pages/LandingPage";
import EmployeeLogin from "./pages/EmployeeLogin";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import MitarbeiterZugang from "./pages/MitarbeiterZugang";
import HaccpHygiene from "./pages/HaccpHygiene";
import DateiImport from "./pages/DateiImport";
import KIAssistent from "./pages/KIAssistent";
import Monatsberichte from "./pages/Monatsberichte";
import Menue from "./pages/Menue";
import PublicMenue from "./pages/PublicMenue";
import StandortVergleich from "./pages/StandortVergleich";
import PanelsDashboard from "./pages/PanelsDashboard";
import Konto from "./pages/Konto";
import { PanelsProvider } from "./hooks/usePanels";
import FeedbackDialog from "./components/FeedbackDialog";
import SettingsDropdown from "./components/SettingsDropdown";
import OnboardingWizard from "./components/OnboardingWizard";
import { TutorialProvider, TutorialStartButton } from "./components/TutorialGuide";
import { Menu, X } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Theme Context - Only Dark Mode
const ThemeContext = createContext(null);

export const useTheme = () => useContext(ThemeContext);

const ThemeProvider = ({ children }) => {
  // Always dark mode
  const [theme] = useState('dark');

  useEffect(() => {
    // Force dark mode
    localStorage.setItem('margevio-theme', 'dark');
    document.documentElement.classList.remove('light');
    document.documentElement.classList.add('dark');
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme: () => {}, toggleTheme: () => {} }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Dashboard Settings Context
const DashboardSettingsContext = createContext(null);

export const useDashboardSettings = () => useContext(DashboardSettingsContext);

const defaultDashboardSettings = {
  showSpeisen: true,
  showGetraenke: true,
  showMitarbeiter: true,
  showFixkosten: true,
  showEinnahmen: true,
  showKostenverteilung: true,
  showGesamtkosten: true,
  showStatistik: true
};

// Module Visibility Context (Sidebar)
const ModuleVisibilityContext = createContext(null);

export const useModuleVisibility = () => useContext(ModuleVisibilityContext);

const defaultModuleVisibility = {
  // Alle Module standardmäßig sichtbar
  speisen: true,
  getraenke: true,
  zutaten: true,
  personal: true,
  fixkosten: true,
  lager: true,
  lieferanten: true,
  schichtplan: true,
  strategie: true,
  preise: true,
  statistik: true,
  einnahmen: true,
  verdienst: true
};

const ModuleVisibilityProvider = ({ children }) => {
  const [visibility, setVisibility] = useState(() => {
    const saved = localStorage.getItem('margevio-module-visibility');
    return saved ? { ...defaultModuleVisibility, ...JSON.parse(saved) } : defaultModuleVisibility;
  });

  useEffect(() => {
    localStorage.setItem('margevio-module-visibility', JSON.stringify(visibility));
  }, [visibility]);

  const updateVisibility = (key, value) => {
    setVisibility(prev => ({ ...prev, [key]: value }));
  };

  const resetVisibility = () => {
    setVisibility(defaultModuleVisibility);
  };

  // Presets für Gastro-Typen
  const applyPreset = (type) => {
    switch (type) {
      case 'bar':
        setVisibility({
          ...defaultModuleVisibility,
          speisen: false,  // Bars haben meist keine Speisen
          schichtplan: true,
        });
        break;
      case 'cafe':
        setVisibility({
          ...defaultModuleVisibility,
          // Café hat alles
        });
        break;
      case 'restaurant':
        setVisibility({
          ...defaultModuleVisibility,
          // Restaurant hat alles
        });
        break;
      default:
        resetVisibility();
    }
  };

  return (
    <ModuleVisibilityContext.Provider value={{ visibility, updateVisibility, resetVisibility, applyPreset }}>
      {children}
    </ModuleVisibilityContext.Provider>
  );
};

const DashboardSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('margevio-dashboard-settings');
    return saved ? JSON.parse(saved) : defaultDashboardSettings;
  });

  useEffect(() => {
    localStorage.setItem('margevio-dashboard-settings', JSON.stringify(settings));
  }, [settings]);

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const resetSettings = () => {
    setSettings(defaultDashboardSettings);
  };

  return (
    <DashboardSettingsContext.Provider value={{ settings, updateSetting, resetSettings }}>
      {children}
    </DashboardSettingsContext.Provider>
  );
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (username, password) => {
    const response = await axios.post(`${API}/auth/login`, {
      username: username.trim(),
      password
    }, { withCredentials: true });
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <div className="text-[#858585]">Laden...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Main App Layout
const AppLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const { user } = useAuth();

  return (
    <TutorialProvider>
      <div className="app-layout dark">
        {/* Mobile overlay */}
        <div 
          className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
          onClick={() => setSidebarOpen(false)}
        />
        
        {/* Sidebar */}
        <Sidebar 
          isOpen={sidebarOpen} 
          onClose={() => setSidebarOpen(false)}
          user={user}
          onFeedbackOpen={() => setFeedbackOpen(true)}
        />

        {/* Feedback Dialog */}
        <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />

        {/* Onboarding Wizard – erscheint nach erstem Login wenn Firmenname leer */}
        <OnboardingWizard user={user} />
        
        {/* Settings Dropdown - now centered with user name */}
        <SettingsDropdown user={user} />
        
        {/* Main Content */}
        <main className="main-content main-content-area">
          <img src="/logo.png" alt="" className="logo-watermark" />
        <Routes>
          <Route index element={<PanelsDashboard />} />
          <Route path="dashboard" element={<PanelsDashboard />} />
          <Route path="dashboard/uebersicht" element={<Dashboard />} />
          <Route path="konto" element={<Konto />} />
          <Route path="admin" element={<AdminDashboard />} />
          <Route path="unternehmerlohn" element={<Unternehmerlohn />} />
          <Route path="einnahmen" element={<Einnahmen />} />
          <Route path="statistik" element={<Statistik />} />
          <Route path="monatsberichte" element={<Monatsberichte />} />
          <Route path="menue" element={<Menue />} />
          <Route path="standort-vergleich" element={<StandortVergleich />} />
          <Route path="preisvorschlag" element={<Preisvorschlag />} />
          <Route path="strategie" element={<Strategie />} />
          <Route path="schichtplan" element={<Schichtplan />} />
          <Route path="haccp" element={<HaccpHygiene />} />
          <Route path="speisen" element={<Speisen />} />
          <Route path="getraenke" element={<Getraenke />} />
          <Route path="mitarbeiter" element={<Mitarbeiter />} />
          <Route path="mitarbeiter-zugang" element={<MitarbeiterZugang />} />
          <Route path="fixkosten" element={<Fixkosten />} />
          <Route path="zutaten" element={<Zutaten />} />
          <Route path="warenwirtschaft" element={<Warenwirtschaft />} />
          <Route path="lieferanten" element={<Lieferanten />} />
          <Route path="import" element={<DateiImport />} />
          <Route path="ki-assistent" element={<KIAssistent />} />
          <Route path="einstellungen" element={<Einstellungen />} />
          <Route path="admin/subscriptions" element={<AdminSubscriptions />} />
          <Route path="admin/backup" element={<AdminBackup />} />
        </Routes>
      </main>
      
      {/* Mobile menu toggle */}
      <button 
        className="mobile-menu-toggle"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="mobile-menu-toggle"
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>
    </div>
    </TutorialProvider>
  );
};

// App Router
const AppRouter = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/mitarbeiter-login" element={<EmployeeLogin />} />
      <Route path="/mitarbeiter-portal" element={<EmployeeDashboard />} />
      <Route path="/m/:token" element={<PublicMenue />} />
      
      {/* Protected Routes */}
      <Route path="/subscription/success" element={
        <ProtectedRoute>
          <SubscriptionSuccess />
        </ProtectedRoute>
      } />
      <Route 
        path="/*" 
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <ModuleVisibilityProvider>
            <DashboardSettingsProvider>
              <AuthProvider>
                <PanelsProvider>
                  <AppRouter />
                  <Toaster position="top-right" />
                </PanelsProvider>
              </AuthProvider>
            </DashboardSettingsProvider>
          </ModuleVisibilityProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
